// /finance — 财务 dashboard 入口
// v1.31 4 模块概览: 应收 / 利润 / 退税 / 现金流
// 设计: 4 大数字卡 + 跳子页 (不堆表格, 真细节去子页)
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ErrorState } from '../components/ErrorState';
import { Icon } from '../components/Icon';
import { CardSkeleton } from '../components/Skeleton';
import { financeApi } from '../lib/api';
import { fmtMoney, fmtMoneyCompact, fmtPctFromValue } from '../lib/format';

export const Route = createFileRoute('/finance/')({
  component: FinanceIndex,
});

// v1.31: 导出 for unit test
export { FinanceIndex };

function FinanceIndex() {
  const ar = useQuery({
    queryKey: ['finance', 'ar', 'summary'],
    queryFn: () => financeApi.arSummary(),
    refetchInterval: 60000,
  });
  const profit = useQuery({
    queryKey: ['finance', 'profit', 'summary', 'month'],
    queryFn: () => financeApi.profitSummary('month'),
    refetchInterval: 120000,
  });
  const tax = useQuery({
    queryKey: ['finance', 'tax-refund', 'summary'],
    queryFn: () => financeApi.taxRefundSummary(),
    refetchInterval: 300000,
  });
  const cash30 = useQuery({
    queryKey: ['finance', 'cashflow', 30],
    queryFn: () => financeApi.cashflow(30),
    refetchInterval: 120000,
  });

  const isInitialLoading = ar.isLoading || profit.isLoading || tax.isLoading || cash30.isLoading;
  const firstError = ar.error ?? profit.error ?? tax.error ?? cash30.error;

  if (firstError) {
    return (
      <ErrorState
        kind="server"
        error={firstError as Error}
        onRetry={() => {
          ar.refetch();
          profit.refetch();
          tax.refetch();
          cash30.refetch();
        }}
      />
    );
  }

  // 找最近 1 个月 (current month) 数据
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentMonth = profit.data?.items.find((it) => it.month === currentMonthKey);
  const currentQuarter = (() => {
    const q = Math.ceil((now.getMonth() + 1) / 3);
    const key = `${now.getFullYear()}-Q${q}`;
    return profit.data?.items.find((it) => it.month === key);
  })();

  // 现金流健康度评分: 0-100
  const health = computeHealthScore(ar.data?.summary, cash30.data);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold mb-1">财务概览</h1>
        <p className="text-ink-3 text-xs sm:text-sm">应收 · 利润 · 退税 · 现金流 · 4 模块一站式</p>
      </div>

      {isInitialLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 应收 */}
          <Link
            to="/finance/ar"
            className="group bg-surface border border-line rounded-lg p-5 hover:border-accent transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="circle-dollar-sign" size="md" className="text-warn" />
                <h2 className="text-sm font-semibold">应收账款</h2>
              </div>
              <Icon
                name="arrow-right"
                size="sm"
                className="text-ink-3 group-hover:text-accent transition-colors"
              />
            </div>
            <div className="text-2xl font-semibold mb-1">
              {fmtMoneyCompact(
                ar.data?.summary.totalOutstandingCents ?? 0,
                ar.data?.summary.currency ?? 'USD',
              )}
            </div>
            <div className="text-xs text-ink-3 mb-3">
              {ar.data?.summary.totalOrders ?? 0} 笔未结清 ·{' '}
              {ar.data?.summary.buckets['90+']?.count ?? 0} 笔超 90 天
            </div>
            <BucketBar summary={ar.data?.summary} />
          </Link>

          {/* 利润 */}
          <Link
            to="/finance/profit"
            className="group bg-surface border border-line rounded-lg p-5 hover:border-accent transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="bar-chart-3" size="md" className="text-good" />
                <h2 className="text-sm font-semibold">利润</h2>
              </div>
              <Icon
                name="arrow-right"
                size="sm"
                className="text-ink-3 group-hover:text-accent transition-colors"
              />
            </div>
            <div className="text-2xl font-semibold mb-1">
              {fmtMoneyCompact(currentMonth?.profitCents ?? 0, currentMonth?.currency ?? 'USD')}
            </div>
            <div className="text-xs text-ink-3 mb-3">
              本月 · {currentMonth?.orderCount ?? 0} 订单 · 利润率{' '}
              {currentMonth ? fmtPctFromValue(currentMonth.avgMargin) : '—'}
            </div>
            <div className="text-[10px] text-ink-3 flex justify-between pt-2 border-t border-line">
              <span>
                本季{' '}
                {fmtMoneyCompact(
                  currentQuarter?.profitCents ?? 0,
                  currentQuarter?.currency ?? 'USD',
                )}
              </span>
              <span>{profit.data?.items.length ?? 0} 月累计</span>
            </div>
          </Link>

          {/* 退税 */}
          <Link
            to="/finance/tax-refund"
            className="group bg-surface border border-line rounded-lg p-5 hover:border-accent transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="receipt" size="md" className="text-info" />
                <h2 className="text-sm font-semibold">出口退税</h2>
              </div>
              <Icon
                name="arrow-right"
                size="sm"
                className="text-ink-3 group-hover:text-accent transition-colors"
              />
            </div>
            <div className="text-2xl font-semibold mb-1">
              {fmtMoneyCompact(
                tax.data?.items.find((it) => it.month === currentMonthKey)?.totalRefundCents ?? 0,
                tax.data?.items.find((it) => it.month === currentMonthKey)?.currency ?? 'CNY',
              )}
            </div>
            <div className="text-xs text-ink-3 mb-3">
              本月预估 · 退税率 {tax.data ? fmtPctFromValue(tax.data.rate * 100, 0) : '—'}
            </div>
            <div className="text-[10px] text-ink-3 flex justify-between pt-2 border-t border-line">
              <span>本年累计 {tax.data?.items.length ?? 0} 月</span>
              <span>CNY 主体</span>
            </div>
          </Link>

          {/* 现金流 */}
          <Link
            to="/finance/cashflow"
            className="group bg-surface border border-line rounded-lg p-5 hover:border-accent transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="trending-up" size="md" className="text-accent" />
                <h2 className="text-sm font-semibold">现金流预测</h2>
              </div>
              <Icon
                name="arrow-right"
                size="sm"
                className="text-ink-3 group-hover:text-accent transition-colors"
              />
            </div>
            <div className="text-2xl font-semibold mb-1">
              {fmtMoneyCompact(
                cash30.data?.expectedInflowCents ?? 0,
                cash30.data?.currency ?? 'USD',
              )}
            </div>
            <div className="text-xs text-ink-3 mb-3">未来 30 天预期入账</div>
            <div className="text-[10px] text-ink-3 flex items-center justify-between pt-2 border-t border-line">
              <span>健康度</span>
              <HealthDot score={health.score} label={health.label} />
            </div>
          </Link>
        </div>
      )}

      <div className="text-[10px] text-ink-3 text-center">
        数据每 1-5 分钟自动刷新 · 跨币种不汇总
      </div>
    </div>
  );
}

function BucketBar({
  summary,
}: { summary?: { buckets: Record<string, { count: number; outstandingCents: number }> } }) {
  const buckets = summary?.buckets;
  if (!buckets) return null;
  const total =
    (buckets['0-30']?.outstandingCents ?? 0) +
    (buckets['31-60']?.outstandingCents ?? 0) +
    (buckets['61-90']?.outstandingCents ?? 0) +
    (buckets['90+']?.outstandingCents ?? 0) +
    (buckets.current?.outstandingCents ?? 0);
  if (total === 0) {
    return <div className="text-[10px] text-ink-3">暂无应收</div>;
  }
  const seg = (key: string, cls: string) => {
    const v = buckets[key]?.outstandingCents ?? 0;
    if (v === 0) return null;
    return (
      <div
        className={cls}
        style={{ width: `${(v / total) * 100}%` }}
        title={`${key}: ${fmtMoney(v, 'USD')}`}
      />
    );
  };
  return (
    <div className="flex h-1.5 bg-surface-2 rounded overflow-hidden">
      {seg('current', 'bg-info')}
      {seg('0-30', 'bg-warn')}
      {seg('31-60', 'bg-warn/70')}
      {seg('61-90', 'bg-bad/70')}
      {seg('90+', 'bg-bad')}
    </div>
  );
}

function HealthDot({ score, label }: { score: number; label: string }) {
  const color = score >= 70 ? 'text-good' : score >= 40 ? 'text-warn' : 'text-bad';
  return (
    <span className={`inline-flex items-center gap-1 ${color} font-medium`}>
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          score >= 70 ? 'bg-good' : score >= 40 ? 'bg-warn' : 'bg-bad'
        }`}
      />
      {label}
    </span>
  );
}

function computeHealthScore(
  summary?: {
    totalOutstandingCents: number;
    buckets: Record<string, { outstandingCents: number }>;
  },
  cash?: { expectedInflowCents: number },
): { score: number; label: string } {
  if (!summary || !cash) return { score: 0, label: '—' };
  const total = summary.totalOutstandingCents;
  if (total === 0) return { score: 100, label: '优' };
  const overdue =
    (summary.buckets['61-90']?.outstandingCents ?? 0) +
    (summary.buckets['90+']?.outstandingCents ?? 0);
  const overdueRatio = overdue / total;
  const inflowRatio = cash.expectedInflowCents / Math.max(total, 1);
  // 30 天内能收回 < 30% = 风险, > 70% = 健康
  let score = 50;
  if (inflowRatio > 0.7) score += 30;
  else if (inflowRatio > 0.4) score += 15;
  else if (inflowRatio < 0.2) score -= 15;
  if (overdueRatio < 0.1) score += 20;
  else if (overdueRatio > 0.3) score -= 20;
  else if (overdueRatio > 0.5) score -= 30;
  score = Math.max(0, Math.min(100, score));
  return { score, label: score >= 70 ? '优' : score >= 40 ? '良' : '警' };
}
